import React from 'react';
import { GraphQLClient } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import {
  GetFeaturedEventsDocument,
  GetFeaturedEventsNoTagDocument,
  GetFeaturedEventsQuery,
  GetFeaturedEventsNoTagQuery
} from '../../../../codegenGenerated/optigraphql/generated';
import Loader from '../Loader';
import { getEnvGQLUrl } from '../../../helpers';
import { getDateDayNumber, getDateThreeLetterMonth } from '../../../helpers/getDateLetter';

export type FeaturedEventsProps = {
  title: string;
  tag: string;
};

export type Facet = {
  AllCategoriesIds: string;
  Categories: {
    Id: string;
    Title: string;
  }[];
};

export type FeatureEvent = {
  EventTitle: string;
  Url: string;
  InitialDateEvent: string;
  EndDateEvent: string;
  EventTypeFacet: Facet;
  EventLocationFacet: Facet;
  EventAudienceFacet: Facet;
};

const useGetFeaturedEvents = (endpoint: string, tag: string) => {
  const graphQLClient = new GraphQLClient(endpoint);
  const browsersDateISO = new Date().toISOString();

  const { isError, error, isFetching, data } = useQuery({
    queryKey: ['GetFeaturedEvents', { tag: tag }],
    queryFn: async () =>
      tag
        ? (graphQLClient.request(GetFeaturedEventsDocument, {
            tag: tag,
            date: browsersDateISO
          }) as GetFeaturedEventsQuery)
        : (graphQLClient.request(GetFeaturedEventsNoTagDocument, {
            date: browsersDateISO
          }) as GetFeaturedEventsNoTagQuery),
    refetchOnWindowFocus: false,
    networkMode: 'always'
  });

  return {
    status: isError ? 'error' : isFetching ? 'loading' : 'success',
    error: error as Error,
    results: data && data.EventDetailPage?.items ? data.EventDetailPage.items : []
  };
};

export const FeaturedEvents = ({ tag }: FeaturedEventsProps) => {
  const { status, results } = useGetFeaturedEvents(getEnvGQLUrl(), tag);

  if (status === 'loading') {
    return <Loader />;
  }

  const getEventType = (item: FeatureEvent) => {
    let eventTypeName = '';

    if (!item) {
      return;
    }

    const { EventAudienceFacet, EventLocationFacet, EventTypeFacet } = item;

    if (
      EventLocationFacet &&
      EventLocationFacet.Categories[0] &&
      EventLocationFacet.Categories[0].Id === tag
    ) {
      eventTypeName = EventLocationFacet.Categories[0].Title;
    }

    if (
      EventAudienceFacet &&
      EventAudienceFacet.Categories[0] &&
      EventAudienceFacet.Categories[0].Id === tag
    ) {
      eventTypeName = EventAudienceFacet.Categories[0].Title;
    }

    if (
      (EventTypeFacet && EventTypeFacet.Categories[0] && EventTypeFacet.Categories[0].Id === tag) ||
      !tag
    ) {
      eventTypeName = EventTypeFacet.Categories[0].Title;
    }

    return eventTypeName;
  };

  return (
    <>
      {status === 'error' && <></>}
      {results.length === 0 && <></>}
      {results.length > 0 && (
        <>
          {results.map(result => (
            <a
              href={result?.Url ? result.Url : '#'}
              className="link featured-event-card"
              key={result?._id}
            >
              <div className="featured-event-card__info">
                <h3 className="featured-event-card__even-title">
                  <span className="featured-event-card__even-title-label">
                    {result?.EventTitle}
                  </span>
                  <div className="link-decorator">
                    <span className="link-decorator-inner"></span>
                    <span className="link-decorator-outer"></span>
                  </div>
                </h3>

                <p className="featured-event-card__event-type">
                  {(result?.EventLocationFacet ||
                    result?.EventAudienceFacet ||
                    result?.EventLocationFacet) && (
                    <span>{getEventType(result as FeatureEvent)}</span>
                  )}
                </p>
              </div>
              {result?.InitialDateEvent && result?.EndDateEvent && (
                <div className="featured-event-card__date">
                  <p className="featured-event-card__date-month">
                    {getDateThreeLetterMonth(result?.InitialDateEvent)}
                    {getDateThreeLetterMonth(result?.InitialDateEvent) !==
                      getDateThreeLetterMonth(result?.EndDateEvent) &&
                      `-${getDateThreeLetterMonth(result?.EndDateEvent)}`}
                  </p>
                  <p className="featured-event-card__date-days">
                    {getDateDayNumber(result?.InitialDateEvent)}
                    {(getDateDayNumber(result?.InitialDateEvent) !==
                      getDateDayNumber(result?.EndDateEvent) ||
                      getDateThreeLetterMonth(result?.InitialDateEvent) !==
                        getDateThreeLetterMonth(result?.EndDateEvent)) &&
                      `-${getDateDayNumber(result?.EndDateEvent)}`}
                  </p>
                </div>
              )}
              {result?.InitialDateEvent && !result?.EndDateEvent && (
                <div className="featured-event-card__date">
                  <p className="featured-event-card__date-month">
                    {getDateThreeLetterMonth(result?.InitialDateEvent)}
                  </p>
                  <p className="featured-event-card__date-days">
                    {getDateDayNumber(result?.InitialDateEvent)}
                  </p>
                </div>
              )}
            </a>
          ))}
        </>
      )}
    </>
  );
};
